import { hasLevel } from 'shared/auth';
import { UserLevel } from 'server/types';

export function setUrlSearchParam(key: string, value: string) {
  const urlParams = new URLSearchParams(location.search);
  urlParams.set(key, value);
  return `?${urlParams.toString()}`;
}

export function addUrlSearchParam(key: string, value: string) {
  return `${location.origin}${location.pathname}${setUrlSearchParam(key, value)}`;
}

export function deleteUrlSearchParam(url: URL, ...keys: string[]) {
  const urlParams = new URL(url).searchParams;
  keys.forEach((k) => urlParams.delete(k));
  return `${url.origin}${url.pathname}?${urlParams.toString()}`;
}

/**
 * Where a signed-in user's "home" is. Four places need this answer -- login,
 * the root redirect, the course page's back-link and the post-checkout
 * destination -- so it lives in one function: they drifted apart once already.
 *
 * The tenant decides first. `isAipMember` is computed globally against the AIP
 * course with no tenant scoping, so it cannot carry a routing decision on its
 * own: a non-Mirasee student is "not an AIP member" like everyone else, and
 * sending them to a community page lands them in Mirasee's shell on someone
 * else's branded site.
 *
 * The guide check applies only to non-members, and only to redirect them off
 * /community/courses -- a student shell that would show a guide who teaches but
 * does not study an empty list. A guide who IS a member keeps /community: it is
 * the page they already use, and there is no link back to it from /courses.
 */
export function communityHomeUrl(opts: {
  isAipMember?: boolean;
  user?: { level: UserLevel };
  tenant?: { isCore?: boolean };
}) {
  if (!opts.tenant?.isCore) {
    return '/courses';
  }
  if (opts.isAipMember === false) {
    // The course list here carries the join / rejoin / access-opens card and a
    // nav offering only what works for a non-member -- but it lists student
    // enrollments and has no Welcome / Overview / Products / Bundles /
    // Upsells, so a guide belongs on their dashboard instead.
    return hasLevel(opts.user, 'guide') ? '/courses' : '/community/courses';
  }
  // Members live in the community feed, guides included: it is the page they
  // already use, and /courses has no link back to it. Unknown membership (the
  // flag fails open to undefined) keeps that historical default.
  return '/community';
}

export function getRedirectUrl(
  user?: { isAipMember?: boolean; level: UserLevel },
  tenant?: { isCore?: boolean },
) {
  const urlParams = new URLSearchParams(location.search);
  const redirect = urlParams.get('redirect');
  if (!redirect || decodeURIComponent(redirect) === '/') {
    return communityHomeUrl({ isAipMember: user?.isAipMember, user, tenant });
  }
  return decodeURIComponent(redirect);
}

export const fullyQualify = (url: string) => `${location.origin}${url}`;
